import type React from 'react';
import { PRIMARY_MODIFIER_LABEL } from '@/utils/keyboardHints.ts';

/**
 * Ctrl/⌘+Enter submits the composer from anywhere inside it.
 *
 * Plain Enter cannot be the shortcut here. The record body and the task details are multi-line
 * fields whose whole job is taking paragraphs, and the two titles already spend Enter moving to the
 * field below. A modifier is the one chord free in every field at once, which is what lets this be
 * bound once around the whole composer rather than per input.
 *
 * `metaKey` alongside `ctrlKey` because ⌘+Enter is the same gesture on a Mac, and a Mac user
 * reaching for Ctrl is reaching for the wrong key rather than a different intent.
 */
export const isComposerSubmitShortcut = (event: React.KeyboardEvent): boolean =>
	event.key === 'Enter' && (event.ctrlKey || event.metaKey);

/**
 * A keydown handler that submits on the shortcut, for the element wrapping a composer's fields.
 *
 * The default is prevented only when the submit actually happens: an incomplete draft has to leave
 * the keystroke alone, since swallowing it would make the chord feel broken rather than premature.
 */
export const composerSubmitKeyDown =
	(canSubmit: boolean, onSubmit: () => void) =>
	(event: React.KeyboardEvent): void => {
		if (!isComposerSubmitShortcut(event) || !canSubmit) return;
		event.preventDefault();
		onSubmit();
	};

/** Announces the chord on the submit control, which is where a screen reader goes looking for it. */
export const COMPOSER_SUBMIT_KEYSHORTCUTS = 'Control+Enter Meta+Enter';

/** The chord as a hint, for the tooltip on the submit pill. */
export const COMPOSER_SUBMIT_SHORTCUT_HINT = `${PRIMARY_MODIFIER_LABEL}+Enter`;
